import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import camelcaseKeys from 'camelcase-keys';
import Tooltip from '@src/components/Tooltip';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { RECAPTCHA_ACTION, RECAPTCHA_TYPE } from '@src/constants/recaptcha';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { RECAPTCHA_ENTERPRISE_SITE_KEY, TTS_SYNTHESIS_URL } from '@src/configs';
import { PING_INTERVAL, WS_TYPE } from '@src/constants/websocket';
import useCustomKeycloak from '@src/hooks/useCustomKeycloak';
import actions from '@src/redux/actions';
import { AUDIO_TYPE, REQUEST_STATUS } from '@src/constants/voice';
import apis from '@src/apis';
import { CircularProgress } from '@mui/material';
import { PACKAGE_CODE } from '@src/constants/package';
import { StyledBoxIcon, StyledIconButton } from './index.style';

const ReadDictionary = ({ invalidInput, hiddenBorder, id, text }) => {
  const growthbook = useGrowthBook();
  const { voice } = useSelector((state) => state.synthesisRequest);
  const ws = useRef(null);
  const { keycloak } = useCustomKeycloak();
  const dispatch = useDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { t } = useTranslation();
  const [previewPayload, setPreviewPayload] = useState({});

  const { playAudioButtonId, isAudioLoading, requestPreviewId } = useSelector(
    (state) => state.dictionary,
  );

  const { user } = useSelector((state) => state.auth);

  const currentReqPreviewId = useRef(requestPreviewId);
  const isSelectedButton = useMemo(
    () => playAudioButtonId === id,
    [playAudioButtonId, id],
  );

  const isDisabled = useMemo(
    () =>
      invalidInput ||
      (isAudioLoading && id !== playAudioButtonId) ||
      user.packageCode === PACKAGE_CODE.STUDIO_FREE,
    [invalidInput, isAudioLoading, id, playAudioButtonId, user.packageCode],
  );

  const handleClickReadDictionary = () => {
    if (playAudioButtonId === id) {
      dispatch(actions.dictionary.updatePlayingAudioStatus(true));
      return;
    }
    dispatch(actions.dictionary.updateLoadingPlayAudioStatus(true));
    dispatch(actions.dictionary.updatePlayAudioButtonId(id));
    setPreviewPayload({
      text,
      voiceCode: voice?.code,
      speed: 1,
      bitrate: 128,
      audioType: AUDIO_TYPE[0], // mp3
      isPronunciationPreview: true,
    });
  };

  const getRecatchaToken = async () => {
    const recaptchaTryListeningTtsType = growthbook.getFeatureValue(
      FEATURE_KEYS.RECAPTCHA_TRY_LISTEN_TTS_TYPE,
    );

    let recaptchaToken;
    switch (recaptchaTryListeningTtsType) {
      case RECAPTCHA_TYPE.V3:
        if (executeRecaptcha)
          recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTION.SYNTHESIS);
        return recaptchaToken;
      case RECAPTCHA_TYPE.ENTERPRISE:
        if (window.grecaptcha) {
          recaptchaToken = await window?.grecaptcha?.enterprise?.execute(
            RECAPTCHA_ENTERPRISE_SITE_KEY,
          );
          return recaptchaToken;
        }
        return '';
      default:
        return '';
    }
  };

  const connectSynthesisWS = async () => {
    if (!previewPayload.text) return;
    const recaptchaToken = await getRecatchaToken();

    ws.current = new WebSocket(TTS_SYNTHESIS_URL);
    let pingInterval;
    ws.current.onopen = () => {
      ws.current.send(
        JSON.stringify({
          type: WS_TYPE.INIT,
          accessToken: keycloak?.token,
        }),
      );
      pingInterval = setInterval(() => {
        ws.current.send(JSON.stringify({ type: WS_TYPE.PING }));
      }, PING_INTERVAL);
    };

    const handleTryListeningError = () => {
      const failurePayload = { sentenceId: '', isAudioLoading: false };
      dispatch(actions.audioPlayer.updateTryListeningSentence(failurePayload));
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'listeningError',
        }),
      );
      dispatch(actions.audioPlayer.updateRequestPreviewId(''));
    };

    ws.current.onmessage = (res) => {
      const responseData = camelcaseKeys(JSON.parse(res.data), { deep: true });
      const {
        type,
        result,
        status,
        // remainingPreview: newRemainingPreview,
      } = responseData;

      if (status === 0) {
        handleTryListeningError();
        dispatch(actions.dictionary.resetPreviewAudio());
      }

      if (type === WS_TYPE.INIT) {
        ws.current.send(
          JSON.stringify({
            type: WS_TYPE.SYNTHESIS,
            payload: previewPayload,
            accessToken: keycloak?.token,
            recaptchaToken,
          }),
        );

        // datasenses.sendPreviewEvent({
        //   userId: user.id,
        //   requestType: REQUEST_TYPE.STUDIO,
        //   voice: previewPayload.voiceCode,
        //   characters: previewPayload.text?.length || 0,
        //   status: REQUEST_STATUS.IN_PROGRESS,
        // });
      }
      if (type === WS_TYPE.SYNTHESIS) {
        switch (result && result.status) {
          case REQUEST_STATUS.IN_PROGRESS: {
            dispatch(
              actions.dictionary.updatePlayAudioRequestId(result.requestId),
            );
            break;
          }
          case REQUEST_STATUS.SUCCESS: {
            const getPublicAudioLink = async (requestId) => {
              const presignedAudioUrlResponse =
                await apis.requests.getPresignedAudioUrl(requestId);

              if (presignedAudioUrlResponse.status)
                return presignedAudioUrlResponse.result.audio;

              return null;
            };

            const playTryListeningAudio = async () => {
              ws.current.send(
                JSON.stringify({ type: WS_TYPE.GET_REMAINING_PREVIEW }),
              );
              if (currentReqPreviewId.current !== result.requestId) return;

              const publicAudioLink = await getPublicAudioLink(
                result.requestId,
              );
              const audioLink = publicAudioLink || result?.audioLink;
              dispatch(actions.dictionary.updatePlayAudioLink(audioLink));
              dispatch(actions.dictionary.updatePlayingAudioStatus(true));
              dispatch(actions.dictionary.updateLoadingPlayAudioStatus(false));
            };

            playTryListeningAudio();

            // disconnect websocket
            ws.current.close();
            break;
          }
          case REQUEST_STATUS.FAILURE: {
            handleTryListeningError();
            dispatch(actions.dictionary.resetPreviewAudio());

            // disconnect websocket
            ws.current.close();
            break;
          }
          default:
            break;
        }
      }

      // if (type === WS_TYPE.GET_REMAINING_PREVIEW)
      // setRemainingPreview(newRemainingPreview);
    };

    ws.current.onclose = (event) => {
      const { code, reason, wasClean } = event;
      // eslint-disable-next-line no-console
      console.log('Websocket is closed', { code, reason, wasClean });
      clearInterval(pingInterval);
    };
  };

  const getTooltipTitle = () => {
    if (user.packageCode === PACKAGE_CODE.STUDIO_FREE)
      return t('premiumFeatureDescription');

    if (isAudioLoading && id !== playAudioButtonId)
      return t('processingOtherRequests');

    return t('preview');
  };

  useEffect(() => {
    if (playAudioButtonId && isSelectedButton && isAudioLoading)
      connectSynthesisWS();
  }, [isAudioLoading, playAudioButtonId]);

  useEffect(() => {
    currentReqPreviewId.current = requestPreviewId;
  }, [requestPreviewId]);

  return (
    <>
      {isAudioLoading && isSelectedButton ? (
        <StyledBoxIcon
          hiddenBorder={!['inputPronunciation', 'inputWord']?.includes(id)}
        >
          <CircularProgress value={25} className="loading-load" size="16px" />
        </StyledBoxIcon>
      ) : (
        <Tooltip arrow title={getTooltipTitle()} placement="top">
          <StyledIconButton
            isDisabled={isDisabled}
            height="40px"
            width="40px"
            borderRadius={
              ['inputPronunciation', 'inputWord']?.includes(id) ? '8px' : '50%'
            }
            isDisableHover
            hiddenBorder={hiddenBorder}
            padding="12px"
            onClick={() => {
              if (isDisabled) return;
              handleClickReadDictionary();
            }}
            data-id="preview-pronunciation"
          >
            <VolumeUpRoundedIcon className="icon" />
          </StyledIconButton>
        </Tooltip>
      )}
    </>
  );
};

export default ReadDictionary;
